import React, { useEffect, useRef, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../../services/api";
import { parseCsv, toCsv, downloadCsv } from "../../../utils/csv";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";

interface DrugRow {
  id: number;
  name: string;
  form?: string;
  strength?: string;
  is_active?: boolean;
}

interface FormState {
  name: string;
  form: string;
  strength: string;
}

export default function DrugList() {
  const { showToast } = useToast();
  const [rows, setRows] = useState<DrugRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const importRef = useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState<FormState>({ name: "", form: "", strength: "" });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);

  const loadIdRef = useRef(0);

  const load = async () => {
    loadIdRef.current += 1;
    const currentLoadId = loadIdRef.current;
    setLoading(true);
    try {
      const data = await apiGet("/api/admin/drugs") as DrugRow[];
      if (currentLoadId !== loadIdRef.current) return;
      const list = Array.isArray(data) ? data : [];
      setRows(list);
    } catch (e) {
      if (currentLoadId !== loadIdRef.current) return;
      showToast({ message: "Failed to load drugs", type: "error" });
    } finally {
      if (currentLoadId === loadIdRef.current) setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleExport = () => {
    if (!rows.length) {
      showToast({ message: "No data to export.", type: "warning" });
      return;
    }
    const headers = ["name", "form", "strength", "is_active"];
    const data = rows.map((r) => ({
      name: r.name,
      form: r.form || "",
      strength: r.strength || "",
      is_active: r.is_active ?? true,
    }));
    downloadCsv(toCsv(headers, data), "drugs.csv");
    showToast({ message: "Export downloaded.", type: "info" });
  };

  const handleImportFile = async (file: File) => {
    const text = await file.text();
    const csvRows = parseCsv(text);
    if (csvRows.length < 2) return showToast({ message: "CSV is empty", type: "error" });
    const headers = csvRows[0].map((h) => h.trim().toLowerCase());
    const dataRows = csvRows.slice(1);

    showToast({ message: "Importing CSV data...", type: "info" });
    let errorCount = 0;

    for (const row of dataRows) {
      const get = (key: string) => {
        const idx = headers.indexOf(key);
        return idx >= 0 ? (row[idx] ?? "").trim() : "";
      };
      const name = get("name");
      if (!name) continue;
      const payload = {
        name,
        form: get("form") || undefined,
        strength: get("strength") || undefined,
      };
      
      try {
        const existing = rows.find((r) => r.name === name);
        if (existing) {
          await apiPut(`/api/admin/drugs/${existing.id}`, payload);
        } else {
          await apiPost("/api/admin/drugs", payload);
        }
      } catch (e) {
        errorCount++;
        console.error("Row import failed", row, e);
      }
    }
    
    if (errorCount > 0) {
      showToast({ message: `Import finished with ${errorCount} errors.`, type: "warning" });
    } else {
      showToast({ message: "Import successful.", type: "success" });
    }
    load();
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return showToast({ message: "Drug Name required", type: "warning" });

    const payload = {
      name: form.name.trim(),
      form: form.form,
      strength: form.strength
    };

    try {
      if (editingId) {
        await apiPut(`/api/admin/drugs/${editingId}`, payload);
        showToast({ message: "Drug Updated", type: "success" });
      } else {
        await apiPost("/api/admin/drugs", payload);
        showToast({ message: "Drug Added", type: "success" });
      }

      setForm({ name: "", form: "", strength: "" });
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (e) {
      showToast({ message: "Operation failed", type: "error" });
    }
  };

  const handleEdit = (row: DrugRow) => {
    setForm({
      name: row.name,
      form: row.form || "",
      strength: row.strength || ""
    });
    setEditingId(row.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setForm({ name: "", form: "", strength: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const toggle = async (row: DrugRow) => {
    try {
      await apiPut(`/api/admin/drugs/${row.id}`, { is_active: !row.is_active });
      load();
    } catch (e) {
      showToast({ message: "Failed to toggle status", type: "error" });
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm("Delete this drug?")) return;
    try {
      await apiDelete(`/api/admin/drugs/${id}`);
      showToast({ message: "Drug deleted", type: "success" });
      load();
    } catch (e) {
      showToast({ message: "Failed to delete drug", type: "error" });
    }
  };

  const filtered = rows.filter(d =>
    d.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="master-panel">
      {/* Toolbar */}
      <div className="master-toolbar">
        <input
          type="search"
          placeholder="Search drugs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="master-search"
        />
        <div className="master-toolbar-actions">
          <input
            ref={importRef}
            type="file"
            accept=".csv"
            className="master-file-input"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) handleImportFile(file);
              if (e.target) e.target.value = "";
            }}
          />
          <Button variant="outline" onClick={() => importRef.current?.click()}>Import CSV</Button>
          <Button variant="outline" onClick={handleExport}>Export CSV</Button>
          <Button variant="primary" onClick={() => { setShowForm(!showForm); if (!showForm) { setForm({ name: "", form: "", strength: "" }); setEditingId(null); } }}>
            {showForm ? "Cancel Form" : "+ Add Drug"}
          </Button>
        </div>
      </div>

      {showForm && (
        <div className={`master-form-row ${editingId ? "is-editing" : ""}`}>
          <div className="master-form-grid master-form-grid-drug">
          <input
            placeholder="Drug Name (e.g. Paracetamol)"
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
            className="master-input"
          />
          <input
            placeholder="Form (Tablet/Syrup)"
            value={form.form}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, form: e.target.value })}
            className="master-input"
          />
          <input
            placeholder="Strength (500mg)"
            value={form.strength}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, strength: e.target.value })}
            className="master-input"
          />

          <div className="master-form-actions">
            <Button variant="primary" onClick={handleSubmit}>
              {editingId ? "💾 Update" : "➕ Add"}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
            )}
          </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="master-table-wrap">
        <table className="master-table">
          <thead className="master-thead">
            <tr>
              <th className="master-th">Name</th>
              <th className="master-th">Form</th>
              <th className="master-th">Strength</th>
              <th className="master-th">Status</th>
              <th className="master-th master-th-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="master-empty">Loading drugs...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="master-empty">No drugs found</td></tr>
            ) : (
              filtered.map(d => (
                <tr key={d.id} className={`master-row ${editingId === d.id ? "is-editing" : ""}`}>
                  <td className="master-cell master-strong">{d.name}</td>
                  <td className="master-cell">{d.form || "—"}</td>
                  <td className="master-cell">{d.strength || "—"}</td>
                  <td className="master-cell">
                    <span 
                      className={`master-status ${d.is_active ? "is-active" : "is-inactive"}`}
                      onClick={() => toggle(d)}
                    >
                      {d.is_active ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td className="master-cell master-cell-right">
                    <div className="master-actions">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(d)}>Edit</Button>
                      <Button variant="danger" size="sm" onClick={() => remove(d.id)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
